import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { adminLogin } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const { login, isAuthenticated, mustChangePassword } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  if (isAuthenticated) {
    return <Navigate to={mustChangePassword ? '/admin/change-password' : '/admin/dashboard'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await adminLogin(username, password);
      if (res.data?.data?.token) {
        // Use context login instead of direct localStorage
        login(res.data.data.token, res.data.data.must_change_password);
      }
    } catch (err) {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid var(--color-border)',
    background: 'rgba(255, 255, 255, 0.05)',
    padding: '12px 14px',
    fontSize: 15,
    color: '#fff',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    borderRadius: 4,
    transition: 'all 0.2s ease',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#999',
    marginBottom: 8,
    fontFamily: 'var(--font-body)',
    fontWeight: 500,
  };

  return (
    <>
      <style>{`
        input::placeholder {
          color: #666;
          opacity: 1;
        }
        input:focus {
          background: rgba(255, 255, 255, 0.08) !important;
        }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: 'var(--color-dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}>
      <div style={{
        background: 'var(--color-surface)',
        padding: '40px 36px',
        width: '100%',
        maxWidth: 360,
        borderRadius: 4,
      }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 32,
          fontWeight: 400,
          textAlign: 'center',
          color: 'var(--color-text)',
          marginBottom: 6,
        }}>
          Admin
        </h1>

        <p style={{
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--color-muted)',
          fontFamily: 'var(--font-body)',
          marginBottom: 28,
        }}>
          Photography portfolio management
        </p>

        {error && (
          <div style={{
            background: '#fff5f5',
            borderLeft: '3px solid var(--color-error)',
            padding: '10px 12px',
            fontSize: 12,
            color: 'var(--color-error)',
            marginBottom: 18,
            fontFamily: 'var(--font-body)',
          }}>
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          {/* Hidden dummy fields to trick Chrome autofill */}
          <input type="text"     style={{ display: 'none' }} readOnly />
          <input type="password" style={{ display: 'none' }} readOnly />

          <div style={{ marginBottom: 22 }}>
            <label style={labelStyle}>Username</label>
            <input
              type="text"
              value={username}
              onChange={e => { setUsername(e.target.value); setError(''); }}
              style={inputStyle}
              autoComplete="new-password"
              spellCheck="false"
              placeholder="Enter username"
              onFocus={(e) => e.target.style.borderColor = 'var(--color-accent)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
            />
          </div>

          <div style={{ marginBottom: 28, position: 'relative' }}>
            <label style={labelStyle}>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                style={{ ...inputStyle, paddingRight: '60px' }}
                autoComplete="new-password"
                placeholder="Enter password"
                onFocus={(e) => e.target.style.borderColor = 'var(--color-accent)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  fontSize: 11,
                  color: '#999',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  padding: '4px 8px',
                  borderRadius: 3,
                  transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.color = '#fff'}
                onMouseLeave={(e) => e.target.style.color = '#999'}
              >
                {showPwd ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 20px',
              background: loading ? '#777' : 'var(--color-accent)',
              color: '#fff',
              border: 'none',
              fontSize: 13,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)',
              borderRadius: 4,
              transition: 'all 0.2s ease',
              fontWeight: 600,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button
              type="button"
              onClick={() => navigate('/admin/forgot-password')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-muted)',
                fontSize: 12,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Forgot password?
            </button>
          </div>
        </form>
      </div>
      </div>
    </>
  );
}